'use client'

import {useEffect} from 'react'
import qs from 'qs'
import {usePageVisibility} from 'react-page-visibility'
import axios from 'axios'
import moment from 'moment'

export const useOauth2Authorized = ({
  authenticateUrlPath,
  authorizeUrlBase,
  redirectUri,
  clientId,
  scope = 'profile,email',
  state,
  oauthCredentials,
  tokenRefreshUrlPath = null,
  handleOauthCredentialsRefreshSuccess = null,
  handleOauthCredentialsRefreshError = null,
  refreshTimeout = 30000
}) => {
  const isVisible = usePageVisibility()
  const isAuthenticated = !!oauthCredentials.accessToken

  function sendToLogin() {
    const oauth2LoginUrl = `${authenticateUrlPath}${qs.stringify(
      {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope,
        ...(state && {state})
      },
      {
        addQueryPrefix: true
      }
    )}`
    window.location.replace(oauth2LoginUrl)
  }

  function getAuthorizeUrl() {
    const authorizeBase = authorizeUrlBase ? `${authorizeUrlBase}/oauth/authorize` : authenticateUrlPath.replace(/\/login$/, '/oauth/authorize')

    return `${authorizeBase}${qs.stringify(
      {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope,
        ...(state && {state})
      },
      {
        addQueryPrefix: true
      }
    )}`
  }

  useEffect(() => {
    if (!isAuthenticated) {
      sendToLogin()
    }
  }, [isAuthenticated])

  useEffect(() => {
    let intervalId = null
    let expiryTimeoutId = null

    if (tokenRefreshUrlPath) {
      if (isAuthenticated) {
        setLogoutTimeout(oauthCredentials)
      }

      if (isAuthenticated && isVisible) {
        intervalId = setInterval(async () => {
          try {
            const {data} = await axios.post(
              `${tokenRefreshUrlPath}`,
              {
                refresh_token: oauthCredentials.refreshToken,
                grant_type: 'refresh_token',
                client_id: clientId
              },
              {
                headers: {
                  Authorization: `Bearer ${oauthCredentials.accessToken}`
                },
                timeout: 2000
              }
            )

            const updatedOauthCredentials = {
              accessToken: data.access_token,
              refreshToken: data.refresh_token,
              expiresAt: data.expires_at
            }

            // Log user out if interval expires.
            setLogoutTimeout(updatedOauthCredentials)

            if (handleOauthCredentialsRefreshSuccess) {
              handleOauthCredentialsRefreshSuccess(updatedOauthCredentials)
            }
          } catch (e) {
            if (handleOauthCredentialsRefreshError) {
              handleOauthCredentialsRefreshError(e)
            }
          }
        }, refreshTimeout)
      } else if (!isAuthenticated || !isVisible) {
        clearTimers()
      }
    }

    function setLogoutTimeout(oc) {
      if (oc?.expiresAt) {
        let time = moment(oc.expiresAt).diff(moment(), 'ms')
        if (expiryTimeoutId) {
          clearTimeout(expiryTimeoutId)
          expiryTimeoutId = null
        }

        expiryTimeoutId = setTimeout(() => {
          if (handleOauthCredentialsRefreshSuccess) {
            handleOauthCredentialsRefreshSuccess({
              accessToken: null,
              refreshToken: null,
              expiresAt: null
            })
          }
          sendToLogin()
        }, time)
      }
    }

    function clearTimers() {
      clearInterval(intervalId)
      clearTimeout(expiryTimeoutId)
      intervalId = null
      expiryTimeoutId = null
    }

    return () => {
      clearTimers()
    }
  }, [isAuthenticated, isVisible])

  return {
    getAuthorizeUrl
  }
}
